import type { QueryClient } from "@tanstack/react-query";
import * as Crypto from "expo-crypto";
import { unwrap } from "@brad-tipper/app-frontend";
import { api } from "~/api/client";

/**
/**
 * Guests have no session and no server row at all: their starter, timers, and
 * progress live only in these three query-cache entries (see query-client.ts
 * for the keys), persisted to AsyncStorage by query-persist.ts so they survive
 * a reload.
 *
 * The moment a guest signs up (src/lib/auth.tsx's `convertToEmailPassword`),
 * this module copies whatever is in these caches into the new account and
 * empties them, so the app's *next* read of guest data starts clean and the
 * *next* read of `["starter", userId]` / etc. is the authoritative source.
 */

export type GuestStarter = {
  id: string;
  name: string;
  lastFedAt: string;
  feedingIntervalHours: number;
  notes: string;
  createdAt: string;
};

export type GuestStarterLog = {
  id: string;
  starterId: string;
  fedAt: string;
  notes: string;
};

export type GuestStarterState = {
  starter: GuestStarter | null;
  logs: GuestStarterLog[];
};

export type GuestProgressEntry = {
  completedSteps: number[];
  activeStep: number;
};

export type GuestProgressMap = Record<string, GuestProgressEntry>;

export type GuestTimer = {
  id: string;
  recipeId: string;
  recipeTitle: string;
  recipeEmoji: string;
  stepIndex: number;
  stepTitle: string;
  startedAt: string;
  notifyAt: string;
  status: "pending" | "sent" | "cancelled" | "failed";
};

export const GUEST_STARTER_QUERY_KEY = ["guestStarter"] as const;
export const GUEST_PROGRESS_QUERY_KEY = ["guestRecipeProgress"] as const;
export const GUEST_TIMERS_QUERY_KEY = ["guestRecipeTimers"] as const;

export const EMPTY_GUEST_STARTER_STATE: GuestStarterState = {
  starter: null,
  logs: [],
};

const newId = () => Crypto.randomUUID();

/**
 * Runs once, right after a guest's sign-up succeeds (the new session is
 * already stored). Migrates any guest starter, timers, and recipe progress
 * into the new account and clears the guest caches. Guest ids are local
 * strings, so every row gets a fresh UUID.
 */
export async function migrateGuestDataOnSignup(
  queryClient: QueryClient,
): Promise<void> {
  const guestStarterState = queryClient.getQueryData<GuestStarterState>(
    GUEST_STARTER_QUERY_KEY,
  );
  if (guestStarterState?.starter) {
    const { starter, logs } = guestStarterState;
    const starterId = newId();
    await unwrap(
      api.putStarter({
        path: { id: starterId },
        body: {
          name: starter.name,
          last_fed_at: starter.lastFedAt,
          feeding_interval_hours: starter.feedingIntervalHours,
          notes: starter.notes,
        },
      }),
    );
    for (const log of logs) {
      await unwrap(
        api.putStarterLog({
          path: { id: newId() },
          body: { starter_id: starterId, fed_at: log.fedAt, notes: log.notes },
        }),
      );
    }
    queryClient.setQueryData<GuestStarterState>(
      GUEST_STARTER_QUERY_KEY,
      EMPTY_GUEST_STARTER_STATE,
    );
  }

  const guestProgress = queryClient.getQueryData<GuestProgressMap>(
    GUEST_PROGRESS_QUERY_KEY,
  );
  const progressEntries = Object.entries(guestProgress ?? {});
  if (progressEntries.length) {
    for (const [recipeId, entry] of progressEntries) {
      await unwrap(
        api.putRecipeProgress({
          path: { id: newId() },
          body: {
            recipe_id: recipeId,
            completed_steps: entry.completedSteps,
            active_step: entry.activeStep,
          },
        }),
      );
    }
    queryClient.setQueryData<GuestProgressMap>(GUEST_PROGRESS_QUERY_KEY, {});
  }

  const guestTimers =
    queryClient.getQueryData<GuestTimer[]>(GUEST_TIMERS_QUERY_KEY) ?? [];
  for (const timer of guestTimers.filter((t) => t.status === "pending")) {
    await unwrap(
      api.putRecipeTimer({
        path: { id: newId() },
        body: {
          recipe_id: timer.recipeId,
          recipe_title: timer.recipeTitle,
          recipe_emoji: timer.recipeEmoji,
          step_index: timer.stepIndex,
          step_title: timer.stepTitle,
          started_at: timer.startedAt,
          notify_at: timer.notifyAt,
          status: timer.status,
        },
      }),
    );
  }
  if (guestTimers.length) {
    queryClient.setQueryData<GuestTimer[]>(GUEST_TIMERS_QUERY_KEY, []);
  }
}
